<template>
<div>
    <div class="form-row my-2">
        <div class="col col-md-4">
            <form-input
                label="Does the employer offer a match?"
                v-model="formData.has_employer_match"
                :errors="errors.has_employer_match"
                type="radio"
                :options="[{text: 'Yes', value: 'yes'}, {text: 'No', value: 'no'}]"
                :noblank="true"
            />
        </div>
    </div>

    <template v-if="hasEmployerMatch">
        <div v-for="(matchTier, idx) in formData.match_tiers" :key="matchTier.id">
            <match-tier-row
                ref="matchTierRows"
                :model-value="matchTier"
                @update:model-value="onTierUpdate(idx, $event)"
                :employee-contribution-label="idx == 0 ? 'For the first (%)' : 'For the next (%)'"
                :show-remove="idx != 0"
                @remove="removeTier(idx)"
            />
        </div>

        <p class="text-center">
            <a href class="btn btn-link" @click.prevent="addTier()">Add another tier</a>
        </p>
    </template>

    <form-errors :errors="formErrors" />
</div>
</template>

<script>
import FormMixin from '@/mixins/Form'
import MatchTierRow from './MatchTierRow'

let nextTierId = 1

export default {
    // The whole employer-match section: the on/off switch plus the ordered list of
    // tiers. One row of that list is a MatchTierRow.
    props: ['initialValue', ],
    mixins: [FormMixin, ],
    components: {MatchTierRow, },
    data() {
        return {
            formData: this.makeFormData(),
        }
    },
    computed: {
        // NB: has_employer_match is a 'yes'/'no' select, so the off state is a *truthy*
        // string. Every guard has to compare, never test truthiness.
        hasEmployerMatch() {
            return this.formData.has_employer_match == 'yes'
        },
    },
    watch: {
        hasEmployerMatch(hasMatch) {
            if (!hasMatch) {
                this.formData.match_tiers = [this.blankTier()]
            }
        },
    },
    methods: {
        blankTier() {
            return {
                id: nextTierId++,
                employee_contribution: '',
                employer_match: '',
            }
        },
        makeFormData() {
            // NB: every row carries an `id`, purely as the v-for key. Rows are replaced
            // wholesale as they are edited, so a row without one would be keyed
            // `undefined` and Vue could not tell the tiers apart.
            const matchTiers = []
            ;(this.initialValue.match_tiers || []).forEach(mt => {
                matchTiers.push({
                    id: nextTierId++,
                    employee_contribution: mt.employee_contribution,
                    employer_match: mt.employer_match,
                })
            })

            if (matchTiers.length < 1) {
                matchTiers.push({
                    id: nextTierId++,
                    employee_contribution: '',
                    employer_match: '',
                })
            }

            return {
                has_employer_match: this.initialValue.has_employer_match ? 'yes' : 'no',
                match_tiers: matchTiers,
            }
        },
        addTier() {
            this.formData.match_tiers.push(this.blankTier())
        },
        removeTier(idx) {
            this.formData.match_tiers = this.formData.match_tiers.filter((mt, i) => {
                return i != idx
            })
        },
        onTierUpdate(idx, value) {
            // Merge rather than replace, so the row keeps the `id` its v-for key uses.
            this.formData.match_tiers[idx] = Object.assign(
                {}, this.formData.match_tiers[idx], value
            )
        },
        validate() {
            this.errors = {}
            this.formErrors = []

            let isValid = true
            if (!this.hasEmployerMatch) {
                return isValid  // No match, so there is nothing to validate.
            }

            if (this.$refs.matchTierRows) {
                this.$refs.matchTierRows.forEach(row => {
                    isValid = row.validate() && isValid
                })
            }

            if (!this.getTiers().length) {
                this.formErrors.push('Add at least one match tier, or turn the employer match off.')
                isValid = false
            }

            return isValid
        },
        getTiers() {
            // NB: read from formData, which MatchTierRow keeps current by emitting on
            // every edit, rather than reaching into $refs.matchTierRows.
            if (!this.hasEmployerMatch) {
                return []
            }

            // Order is meaningful: the tiers are incremental bands, and the server
            // assigns each row's priority from its position in this list.
            return this.formData.match_tiers.map(mt => ({
                employee_contribution: mt.employee_contribution,
                employer_match: mt.employer_match,
            }))
        },
        getFormData() {
            return {
                has_employer_match: this.hasEmployerMatch,
                match_tiers: this.getTiers(),
            }
        },
    },
}
</script>
